import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { createTranslator } from "next-intl";
import content from "../../content/claims.json";
import { MainLayout } from "../../layouts/MainLayout";
import { Loader } from "../../shared/Loader";
import { useTenantTheme } from "../../themes/TenantThemeProvider";
import { Card } from "../../shared/Card";
import { getClaim } from "../../api/getClaim";
import { getStatusViewModel } from "./getStatusViewModel";
import { StatusHero } from "./components/StatusHero";
import { ClaimStepper } from "./components/ClaimStepper";
import { ResolutionPanel } from "./components/ResolutionPanel";
import { ClaimItemsList } from "./components/ClaimItemsList";
import { OrderAndClaimDetails } from "./components/OrderAndClaimDetails";
import { ShippingAddressCard } from "./components/ShippingAddressCard";
import { PhotosSection } from "./components/PhotosSection";
import { MerchantSupportCard } from "./components/MerchantSupportCard";
import { CoverageSummary } from "./components/CoverageSummary";
import { UploadDocumentsCard } from "./components/UploadDocumentsCard";

export function Status() {
  const { isThemeLoading, isAppLoading, setIsAppLoading, locale } =
    useTenantTheme();
  const { claimNumber } = useParams();
  const [claim, setClaim] = useState();

  const t = createTranslator({
    messages: content[locale],
  });

  useEffect(() => {
    setIsAppLoading(true);

    async function fetchClaim() {
      try {
        const response = await getClaim(claimNumber);

        if (response.data) {
          setClaim(response.data);
        }
      } finally {
        setIsAppLoading(false);
      }
    }

    fetchClaim();
  }, [claimNumber, locale, setIsAppLoading]);

  if (isThemeLoading || isAppLoading) {
    return <Loader />;
  }

  if (!claim) {
    return (
      <MainLayout reduceBackgroundNoise hideHelp compactFooter>
        <Card title={t("status.notFound")} compact />
      </MainLayout>
    );
  }

  const viewModel = getStatusViewModel(claim, t);
  const supportEmail = claim.merchant?.support_email;

  return (
    <MainLayout reduceBackgroundNoise hideHelp compactFooter>
      <Card compact>
        <StatusHero
          hero={viewModel.hero}
          claimNumber={claim.claim_number}
          nextSteps={viewModel.nextSteps}
          notificationEmail={viewModel.notificationEmail}
          statusAnnouncement={viewModel.statusAnnouncement}
          t={t}
        />
        <ClaimStepper steps={viewModel.stepperSteps} t={t} />
        {viewModel.showUploadDocuments ? (
          <UploadDocumentsCard
            supportEmail={supportEmail}
            claimNumber={claim.claim_number}
            t={t}
          />
        ) : null}
        <ResolutionPanel resolution={viewModel.resolution} t={t} />
        {viewModel.showShippingAddress ? (
          <ShippingAddressCard address={claim.shipping_address} t={t} />
        ) : null}
        <OrderAndClaimDetails claim={claim} t={t} />
        {viewModel.showClaimItems ? (
          <ClaimItemsList items={claim.claim_items} claim={claim} t={t} />
        ) : null}
        <PhotosSection imageUrls={claim.image_urls} t={t} />
        <CoverageSummary
          claim={claim}
          isAwaitingReview={viewModel.isAwaitingReview}
          t={t}
        />
        <MerchantSupportCard
          merchant={claim.merchant}
          claimNumber={claim.claim_number}
          isAwaitingReview={viewModel.isAwaitingReview}
          t={t}
        />
      </Card>
    </MainLayout>
  );
}
